import React, { useState } from 'react';
import { useGamification } from '../contexts/GamificationContext';
import GamificationDashboard from '../components/gamification/GamificationDashboard';
import BadgeIcon from '../components/BadgeIcon';
import { 
  Trophy, 
  Award, 
  Users, 
  Target,
  Star,
  Crown,
  Medal,
  Zap
} from 'lucide-react';

const Gamification = () => {
  const {
    badges,
    leaderboard,
    getUnlockedBadges,
    getLockedBadges,
    getBadgesByCategory,
    getRarityColor,
    getRarityName
  } = useGamification();
  
  const [activeTab, setActiveTab] = useState('dashboard');

  const unlockedBadges = getUnlockedBadges();
  const lockedBadges = getLockedBadges();
  const categories = [...new Set(badges.map(badge => badge.category))];

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'learning':
        return <Target className="w-5 h-5" />;
      case 'streak':
        return <Zap className="w-5 h-5" />;
      case 'community':
        return <Users className="w-5 h-5" />;
      case 'performance':
        return <Star className="w-5 h-5" />;
      case 'exploration':
        return <Award className="w-5 h-5" />;
      case 'milestone':
        return <Crown className="w-5 h-5" />;
      default:
        return <Trophy className="w-5 h-5" />;
    }
  };

  const getCategoryName = (category) => {
    switch (category) {
      case 'learning':
        return 'Apprentissage';
      case 'streak':
        return 'Série';
      case 'community':
        return 'Communauté';
      case 'performance':
        return 'Performance';
      case 'exploration':
        return 'Exploration';
      case 'milestone':
        return 'Jalons';
      default:
        return 'Général';
    }
  };

  const tabs = [
    { id: 'dashboard', name: 'Tableau de bord', icon: Trophy },
    { id: 'badges', name: 'Badges', icon: Award },
    { id: 'leaderboard', name: 'Classement', icon: Users }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Gamification
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Transformez votre apprentissage en aventure ! Gagnez des points, débloquez des badges et grimpez dans le classement.
        </p>
      </div>

      {/* Navigation par onglets */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-primary border-b-2 border-primary dark:text-primary-400'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {/* Tableau de bord */}
          {activeTab === 'dashboard' && <GamificationDashboard />}

          {/* Badges */}
          {activeTab === 'badges' && (
            <div className="space-y-6">
              {/* Statistiques des badges */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-2xl font-bold">{unlockedBadges.length}</div>
                      <div className="text-sm opacity-90">Badges obtenus</div>
                    </div>
                    <Award className="w-8 h-8 text-green-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-2xl font-bold">{lockedBadges.length}</div>
                      <div className="text-sm opacity-90">Badges à débloquer</div>
                    </div>
                    <Target className="w-8 h-8 text-blue-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-2xl font-bold">
                        {Math.round((unlockedBadges.length / badges.length) * 100)}%
                      </div>
                      <div className="text-sm opacity-90">Progression</div>
                    </div>
                    <Trophy className="w-8 h-8 text-purple-200" />
                  </div>
                </div>
              </div>

              {/* Badges par catégorie */}
              {categories.map((category) => {
                const categoryBadges = getBadgesByCategory(category);
                const unlockedInCategory = categoryBadges.filter(badge => badge.unlocked);
                
                return (
                  <div key={category} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-4">
                      {getCategoryIcon(category)}
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {getCategoryName(category)}
                      </h3>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        ({unlockedInCategory.length}/{categoryBadges.length})
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                      {categoryBadges.map((badge) => (
                        <div
                          key={badge.id}
                          className={`text-center p-4 rounded-lg transition-all duration-200 ${
                            badge.unlocked
                              ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:shadow-md'
                              : 'bg-gray-100 dark:bg-gray-600 opacity-60'
                          }`}
                        >
                          <div className="text-3xl mb-2 filter grayscale-0">
                            <BadgeIcon icon={badge.icon} />
                          </div>
                          <div className={`text-sm font-medium mb-1 ${
                            badge.unlocked 
                              ? 'text-gray-900 dark:text-white' 
                              : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {badge.name}
                          </div>
                          <div className={`text-xs px-2 py-1 rounded-full ${getRarityColor(badge.rarity)}`}>
                            {getRarityName(badge.rarity)}
                          </div>
                          <div className={`text-xs mt-1 ${
                            badge.unlocked 
                              ? 'text-gray-600 dark:text-gray-400' 
                              : 'text-gray-500 dark:text-gray-500'
                          }`}>
                            +{badge.points} pts
                          </div>
                          {badge.unlocked && badge.unlockedAt && (
                            <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                              ✓ Débloqué
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Classement */}
          {activeTab === 'leaderboard' && (
            <div className="space-y-6">
              {/* Top 3 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {leaderboard.slice(0, 3).map((user, index) => {
                  const rankColors = [
                    'from-yellow-400 to-yellow-600',
                    'from-gray-300 to-gray-500',
                    'from-orange-400 to-orange-600'
                  ];
                  
                  return (
                    <div
                      key={user.id}
                      className={`bg-gradient-to-br ${rankColors[index]} rounded-lg p-6 text-white text-center ${
                        index === 0 ? 'transform scale-105' : ''
                      }`}
                    >
                      <div className="relative mb-4">
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="w-16 h-16 rounded-full mx-auto border-4 border-white/30"
                        />
                        {index === 0 && <Crown className="w-6 h-6 absolute -top-2 -right-2 text-yellow-300" />}
                        {index === 1 && <Medal className="w-6 h-6 absolute -top-2 -right-2 text-gray-200" />}
                        {index === 2 && <Award className="w-6 h-6 absolute -top-2 -right-2 text-orange-200" />}
                      </div>
                      <h3 className="font-bold text-lg mb-1">{user.name}</h3>
                      <div className="text-sm opacity-90 mb-2">
                        Niveau {user.level}
                      </div>
                      <div className="text-2xl font-bold mb-1">
                        {user.points} pts
                      </div>
                      <div className="text-xs opacity-80">
                        {user.coursesCompleted} cours • {user.streak} jours
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Classement complet */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Classement Complet
                  </h3>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {leaderboard.map((user) => (
                    <div
                      key={user.id}
                      className={`p-4 flex items-center gap-4 ${
                        user.isCurrentUser 
                          ? 'bg-primary/10 dark:bg-primary/20' 
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        user.rank === 1 ? 'bg-yellow-500 text-white' :
                        user.rank === 2 ? 'bg-gray-400 text-white' :
                        user.rank === 3 ? 'bg-orange-500 text-white' :
                        'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                      }`}>
                        {user.rank}
                      </div>
                      
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Niveau {user.level} • {user.coursesCompleted} cours
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {user.points} pts
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {user.streak} jours
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Gamification;
